import React, { useState, createContext, useContext, useCallback, useMemo, useEffect } from "react";

const initialState = {
  authToken: null,
  appLogout: false, // Start simple - always false initially
  showMessagePanel: true,
  apiResponseData: null,
  caseId: "",
  initialCaseSelect: false,
  detailsStatus: "",
  caseStatusReason: "",
  lastModifiedBy: "", 
  status: {
    mail: false,
    casestatus: "",
    mailContent: "",
  },
  prompt: true,
  promptQuery: "",
  // Stores the last user-initiated query (used by Refresh). Unlike promptQuery,
  // this is NOT cleared on back-navigation so Refresh always knows what to re-run.
  lastRefreshableQuery: "",
  // Incremented each time a prompt is clicked so identical queries still fire
  promptQueryNonce: 0,
  previousSendMessage: false,
  remarksReason: "",
  activeTab: "cargo-mate", // Add active tab state
  loading: false, // Add loading state for prompt queries
  selectedCountry: "",
  navigationHistory: {
    previousView: null, // Store previous view data to enable back navigation
    canGoBack: false
  },
  tabTypeName: "",
  isCaseSummaryView: false,
  preselectedNextAction: "",
  preselectedNextActionMeta: null,

};

const StoreContext = createContext(initialState);

export const StoreProvider = ({ children }) => {
  const [store, setStore] = useState(initialState);

  useEffect(() => {
    const syncAuthToken = () => {
      try {
        const token = sessionStorage.getItem("authToken");
        setStore((prev) => {
          if (prev.authToken === token) {
            return prev;
          }
          return { ...prev, authToken: token };
        });
      } catch {
        setStore((prev) => (prev.authToken === null ? prev : { ...prev, authToken: null }));
      }
    };

    syncAuthToken();
    window.addEventListener("authStateChanged", syncAuthToken);
    window.addEventListener("storage", syncAuthToken);

    return () => {
      window.removeEventListener("authStateChanged", syncAuthToken);
      window.removeEventListener("storage", syncAuthToken);
    };
  }, []);

  const updateStore = useCallback((updates) => {
    setStore((prevState) => {
      let hasChange = false;
      for (const key of Object.keys(updates || {})) {
        if (!Object.is(prevState[key], updates[key])) {
          hasChange = true;
          break;
        }
      }

      if (!hasChange) {
        return prevState;
      }

      return { ...prevState, ...updates };
    });
  }, []);

  const contextValue = useMemo(() => ({ store, updateStore }), [store, updateStore]);

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  return useContext(StoreContext);
};
